/**
 * Media type utilities
 *
 * Files in the gallery can be images or videos. A video file record reuses the
 * same thumbnail fields as an image (`url.original`, `url.srcset`, `dimensions`)
 * so existing thumbnail rendering works. Videos additionally carry:
 *   - mediaType: 'video'
 *   - url.video: path to the playable video file (served from /images/...)
 *   - url.posters: optional extra poster frames for cycling previews
 *
 * Appearance-era uploads may store the mp4 on `url.original` and a still on
 * `url.poster` without setting `mediaType`; helpers below cover both shapes.
 * Legacy image records have no `mediaType` and are treated as images.
 */

export const MEDIA_TYPE_IMAGE = 'image';
export const MEDIA_TYPE_VIDEO = 'video';

export const VIDEO_MIME_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
export const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'm4v'];

function extensionOf(pathLike) {
    if (!pathLike || typeof pathLike !== 'string') return '';
    const base = pathLike.split(/[?#]/)[0];
    const parts = base.split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
}

function isVideoPath(pathLike) {
    return VIDEO_EXTENSIONS.includes(extensionOf(pathLike));
}

/**
 * Resolve the media type for a file record, defaulting to image for legacy records.
 * @param {Object} file - File record from the database
 * @returns {'image'|'video'}
 */
export function getMediaType(file) {
    if (!file) return MEDIA_TYPE_IMAGE;
    if (file.mediaType === MEDIA_TYPE_VIDEO) return MEDIA_TYPE_VIDEO;
    if (typeof file.mimetype === 'string' && file.mimetype.startsWith('video/')) {
        return MEDIA_TYPE_VIDEO;
    }
    if (file.url?.video) return MEDIA_TYPE_VIDEO;
    if (isVideoPath(file.url?.original)) return MEDIA_TYPE_VIDEO;
    return MEDIA_TYPE_IMAGE;
}

/**
 * @param {Object} file
 * @returns {boolean} True when the file is a video.
 */
export function isVideo(file) {
    return getMediaType(file) === MEDIA_TYPE_VIDEO;
}

/**
 * Encode each path segment so filenames with `&`, spaces, etc. stay in the path
 * (browsers otherwise treat `&…` as a query string).
 * @param {string} relativePath
 * @returns {string} absolute URL path
 */
export function toPublicMediaUrl(relativePath) {
    if (!relativePath || typeof relativePath !== 'string') return '';
    const raw = relativePath.startsWith('/') ? relativePath.slice(1) : relativePath;
    return `/${raw.split('/').map((segment) => encodeURIComponent(segment)).join('/')}`;
}

/**
 * Get the playable video URL (with leading slash) for a video file, or null.
 * @param {Object} file
 * @returns {string|null}
 */
export function getVideoUrl(file) {
    if (!isVideo(file)) return null;
    const raw = file?.url?.video
        || (isVideoPath(file?.url?.original) ? file.url.original : null);
    if (!raw) return null;
    return toPublicMediaUrl(raw);
}

/**
 * Ordered poster relative paths for a video (primary first), used for cycling
 * thumbnail previews. Falls back to the primary still when present.
 * @param {Object} file
 * @returns {string[]} relative paths (no leading slash normalization)
 */
export function getPosterPaths(file) {
    const paths = [];
    const push = (p) => {
        if (p && typeof p === 'string' && !isVideoPath(p) && !paths.includes(p)) {
            paths.push(p);
        }
    };

    push(file?.url?.poster);
    push(file?.url?.original);
    const extra = Array.isArray(file?.url?.posters) ? file.url.posters : [];
    extra.forEach((poster) => {
        const p = typeof poster === 'string' ? poster : poster?.original;
        push(p);
    });
    return paths;
}

/**
 * Detect whether an uploaded file (browser File or server multer file) is a video.
 * @param {{ mimetype?: string, type?: string, originalname?: string, name?: string }} file
 * @returns {boolean}
 */
export function isVideoUpload(file) {
    if (!file) return false;
    const mime = file.mimetype || file.type || '';
    if (mime.startsWith('video/')) return true;
    const name = file.originalname || file.name || '';
    const ext = name.split('.').pop()?.toLowerCase();
    return VIDEO_EXTENSIONS.includes(ext);
}

/**
 * Dropzone `accept` map covering both JPEG images and supported videos.
 */
export const IMAGE_AND_VIDEO_ACCEPT = {
    'image/jpeg': [],
    'video/mp4': ['.mp4', '.m4v'],
    'video/webm': ['.webm'],
    'video/quicktime': ['.mov'],
};

export default {
    MEDIA_TYPE_IMAGE,
    MEDIA_TYPE_VIDEO,
    VIDEO_MIME_TYPES,
    VIDEO_EXTENSIONS,
    getMediaType,
    isVideo,
    getVideoUrl,
    getPosterPaths,
    isVideoUpload,
    IMAGE_AND_VIDEO_ACCEPT,
    toPublicMediaUrl,
};
